/**
 * AuthService - Supabase authentication abstraction
 * 
 * Handles OAuth login/logout, profile management, and session state.
 * UI-specific code (DOM manipulation) stays in app.js.
 */

// Supabase configuration (public keys, safe to expose)
const SUPABASE_URL = 'https://qetqrfddeicpnvvbjrvy.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFldHFyZmRkZWljcG52dmJqcnZ5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE3OTQ3MTMsImV4cCI6MjA4NzM3MDcxM30.DrJ0VaKpgj4epb6XyhJAG_00Jo5aG6x2P_ehK-HlOFU';

/**
 * Creates an AuthService instance
 * 
 * @param {Object} callbacks - Callback functions for auth events
 * @param {Function} callbacks.onAuthStateChange - Called when auth state changes (event, session, userProfile)
 * @param {Function} callbacks.onError - Called on auth errors (error)
 * @returns {Object} AuthService instance
 */
export function createAuthService(callbacks = {}) {
    const { onAuthStateChange, onError } = callbacks;

    // Internal state
    let supabaseClient = null;
    let currentUser = null;
    let userProfile = null;

    /**
     * Initialize Supabase client
     * @returns {boolean} True if initialized successfully
     */
    function initSupabase() {
        if (typeof window.supabase !== 'undefined' && window.supabase.createClient) {
            supabaseClient = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
            return true;
        }
        console.warn('Supabase SDK not loaded');
        return false;
    }

    /**
     * Initialize auth and set up listeners
     */
    async function init() {
        if (!initSupabase()) {
            console.warn('Auth disabled - Supabase not available');
            return;
        }

        // Check for existing session
        const { data: { session } } = await supabaseClient.auth.getSession();
        if (session) {
            await handleAuthChange('SIGNED_IN', session);
        }

        // Listen for auth state changes
        supabaseClient.auth.onAuthStateChange(async (event, session) => {
            await handleAuthChange(event, session);
        });
    }

    /**
     * Handle auth state changes
     */
    async function handleAuthChange(event, session) {
        console.log('Auth event:', event);

        if (event === 'SIGNED_IN' && session) {
            currentUser = session.user;
            // Ensure profile exists before any other operations
            await ensureProfileExists();
            // Load profile data
            await loadUserProfile();
            if (onAuthStateChange) {
                onAuthStateChange(event, session, userProfile);
            }
        } else if (event === 'SIGNED_OUT') {
            currentUser = null;
            userProfile = null;
            if (onAuthStateChange) {
                onAuthStateChange(event, null, null);
            }
        } else if (onAuthStateChange) {
            // Forward other events too
            onAuthStateChange(event, session, userProfile);
        }
    }

    // -------- Profile Management --------

    /**
     * Ensure profile row exists for current user
     * Called on SIGNED_IN to prevent FK violations when saving patches
     */
    async function ensureProfileExists() {
        if (!supabaseClient || !currentUser) return;

        try {
            // Upsert profile - creates if not exists, does nothing if exists
            const { error } = await supabaseClient
                .from('profiles')
                .upsert({
                    id: currentUser.id,
                    updated_at: new Date().toISOString()
                }, { 
                    onConflict: 'id',
                    ignoreDuplicates: true 
                });

            if (error) {
                console.error('Error ensuring profile exists:', error);
            }
        } catch (err) {
            console.error('Error ensuring profile exists:', err);
        }
    }

    /**
     * Load user profile from database
     */
    async function loadUserProfile() {
        if (!supabaseClient || !currentUser) return null;

        try {
            const { data, error } = await supabaseClient
                .from('profiles')
                .select('display_name, avatar_url')
                .eq('id', currentUser.id)
                .maybeSingle();

            if (error) {
                console.error('Error loading profile:', error);
                return null;
            }

            userProfile = data || { display_name: null, avatar_url: null };
            return userProfile;
        } catch (err) {
            console.error('Error loading profile:', err);
            return null;
        }
    }

    /**
     * Save user profile to database
     * @param {string|null} displayName - Display name or null to clear
     * @param {string|null} avatarUrl - Avatar URL or null to use Discord
     * @returns {Promise<boolean>} Success
     */
    async function saveUserProfile(displayName, avatarUrl) {
        if (!supabaseClient || !currentUser) {
            if (onError) onError(new Error('Not logged in'));
            return false;
        }

        try {
            const { error } = await supabaseClient
                .from('profiles')
                .upsert({
                    id: currentUser.id,
                    display_name: displayName || null,
                    avatar_url: avatarUrl || null,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'id' });

            if (error) {
                console.error('Error saving profile:', error);
                if (onError) onError(error);
                return false;
            }

            userProfile = { display_name: displayName, avatar_url: avatarUrl };
            return true;
        } catch (err) {
            console.error('Error saving profile:', err);
            if (onError) onError(err);
            return false;
        }
    }

    /**
     * Get effective display name (profile > Discord > email)
     */
    function getDisplayName() {
        if (userProfile?.display_name) return userProfile.display_name;
        if (currentUser) {
            const meta = currentUser.user_metadata || {};
            return meta.full_name || meta.name || meta.preferred_username || currentUser.email?.split('@')[0] || 'User';
        }
        return 'User';
    }

    /**
     * Get effective avatar URL (profile > Discord)
     */
    function getAvatarUrl() {
        if (userProfile?.avatar_url) return userProfile.avatar_url;
        if (currentUser) {
            const meta = currentUser.user_metadata || {};
            return meta.avatar_url || meta.picture || '';
        }
        return '';
    }

    /**
     * Get Discord avatar URL (ignores profile override)
     */
    function getDiscordAvatarUrl() {
        if (currentUser) {
            const meta = currentUser.user_metadata || {};
            return meta.avatar_url || meta.picture || '';
        }
        return '';
    }

    /**
     * Get Discord name (ignores profile override)
     */
    function getDiscordName() {
        if (currentUser) {
            const meta = currentUser.user_metadata || {};
            return meta.full_name || meta.name || meta.preferred_username || currentUser.email?.split('@')[0] || 'User';
        }
        return 'User';
    }

    // -------- Auth Actions --------

    /**
     * Login with Discord OAuth
     * @returns {Promise<{success: boolean, error: Error|null}>}
     */
    async function loginWithDiscord() {
        if (!supabaseClient) {
            const error = new Error('Auth not available');
            if (onError) onError(error);
            return { success: false, error };
        }

        const { error } = await supabaseClient.auth.signInWithOAuth({
            provider: 'discord',
            options: {
                redirectTo: window.location.origin + window.location.pathname
            }
        });

        if (error) {
            console.error('Login error:', error);
            if (onError) onError(error);
            return { success: false, error };
        }

        return { success: true, error: null };
    }

    /**
     * Logout
     * @returns {Promise<{success: boolean, error: Error|null}>}
     */
    async function logout() {
        if (!supabaseClient) {
            return { success: false, error: new Error('Auth not available') };
        }

        const { error } = await supabaseClient.auth.signOut();

        if (error) {
            console.error('Logout error:', error);
            if (onError) onError(error);
            return { success: false, error };
        }

        return { success: true, error: null };
    }

    /**
     * Delete all user data (patches, favorites, profile) and sign out
     * @returns {Promise<{success: boolean, errors: Error[]}>}
     */
    async function deleteAccount() {
        if (!supabaseClient || !currentUser) {
            return { success: false, errors: [new Error('Not logged in')] };
        }

        const errors = [];

        try {
            // Delete all user's patches
            const { error: patchError } = await supabaseClient
                .from('patches')
                .delete()
                .eq('user_id', currentUser.id);

            if (patchError) {
                console.error('Error deleting patches:', patchError);
                errors.push(patchError);
            }

            // Delete all user's favorites
            const { error: favError } = await supabaseClient
                .from('favorites')
                .delete()
                .eq('user_id', currentUser.id);

            if (favError) {
                console.error('Error deleting favorites:', favError);
                errors.push(favError);
            }

            // Delete user's profile
            const { error: profileError } = await supabaseClient
                .from('profiles')
                .delete()
                .eq('id', currentUser.id);

            if (profileError) {
                console.error('Error deleting profile:', profileError);
                errors.push(profileError);
            }

            // Sign out
            await supabaseClient.auth.signOut();

            return { success: errors.length === 0, errors };
        } catch (err) {
            console.error('Error deleting account:', err);
            errors.push(err);
            return { success: false, errors };
        }
    }

    // -------- Getters --------

    /**
     * Get current user object
     */
    function getCurrentUser() {
        return currentUser;
    }

    /**
     * Get cached user profile
     */
    function getUserProfile() {
        return userProfile;
    }

    /**
     * Get Supabase client (for direct queries if needed)
     */
    function getSupabaseClient() {
        return supabaseClient;
    }

    /**
     * Check if user is logged in
     */
    function isLoggedIn() {
        return !!currentUser;
    }

    // -------- Public API --------

    return {
        // Initialization
        init,

        // Auth actions
        loginWithDiscord,
        logout,
        deleteAccount,

        // Profile management
        loadUserProfile,
        saveUserProfile,
        getDisplayName,
        getAvatarUrl,
        getDiscordAvatarUrl,
        getDiscordName,

        // State getters
        getCurrentUser,
        getUserProfile,
        getSupabaseClient,
        isLoggedIn
    };
}
